import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { canWriteArea, localDayKey, parseDbTimestamp, type PublicUserDTO, type StatusAccess } from '@wf/shared'
import { formatDateDE } from '../time/format'

// Globales Abo-Banner (W5): eine ruhige, aber unübersehbare Zeile über der Kopfleiste, gespeist aus status.access
// (alle Rollen). Zwei Fälle:
//   • Einschränkung (readonly/billing_only) → ALLE sehen sie (betrifft das Arbeiten aller); der Server erzwingt die
//     Schreibsperre ohnehin, das Banner erklärt sie.
//   • Vorwarnung im vollen Zustand (Zahlung fehlgeschlagen in Kulanz / Testphase endet bald) → nur die Abo-
//     Verwalter (Chef), weil nur sie handeln können — Mitarbeiter sollen nicht unnötig beunruhigt werden.

const DAY_MS = 86_400_000
/** Ab so vielen Resttagen wird der Testphasen-Auslauf angekündigt. */
const TRIAL_HINT_DAYS = 5

function Banner({ tone, children }: { tone: 'warn' | 'info'; children: ReactNode }) {
  return (
    <div className={`wf-appbanner wf-appbanner--${tone}`} role={tone === 'warn' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

/** Rendert das passende Banner — oder null, wenn nichts anzuzeigen ist. */
export function AccessBanner({ access, user }: { access: StatusAccess; user: PublicUserDTO }) {
  const canManage = canWriteArea(user, 'werkstatt_einstellungen')
  const cta = canManage ? (
    <Link to="/einstellungen/abo" className="wf-appbanner__cta">
      Abo verwalten
    </Link>
  ) : null

  // 1) Harte Einschränkung — für alle sichtbar.
  if (access.level === 'billing_only') {
    return (
      <Banner tone="warn">
        <span>Cevio ist gesperrt: Das Abo ist beendet — nur noch Lesen und die Abo-Verwaltung sind möglich.</span>
        {cta}
      </Banner>
    )
  }
  if (access.level === 'readonly') {
    return (
      <Banner tone="warn">
        <span>Cevio ist eingeschränkt: Wegen einer offenen Zahlung sind Änderungen gesperrt (nur Lesen).</span>
        {cta}
      </Banner>
    )
  }

  // 2) Vorwarnungen im vollen Zustand — nur für Abo-Verwalter (handeln kann nur der Chef).
  if (!canManage) return null
  if (access.billingStatus === 'past_due') {
    return (
      <Banner tone="warn">
        <span>Die letzte Zahlung ist fehlgeschlagen. Bitte den Zahlweg prüfen, sonst wird Cevio bald auf „Nur Lesen“ gestellt.</span>
        {cta}
      </Banner>
    )
  }
  if (access.billingStatus === 'trialing' && access.trialEnd) {
    const daysLeft = Math.ceil((parseDbTimestamp(access.trialEnd) - Date.now()) / DAY_MS)
    if (daysLeft <= TRIAL_HINT_DAYS) {
      const when = daysLeft <= 0 ? 'heute' : `in ${daysLeft} Tag${daysLeft === 1 ? '' : 'en'}`
      return (
        <Banner tone="info">
          <span>
            Deine Testphase endet {when} ({formatDateDE(localDayKey(parseDbTimestamp(access.trialEnd)))}). Richte einen
            Zahlweg ein, um ohne Unterbrechung weiterzuarbeiten.
          </span>
          {cta}
        </Banner>
      )
    }
  }
  return null
}
